import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import MediaSlot from "@/components/MediaSlot";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { CREATIVE_INTRO, INSTAGRAM, LINKS, PROJECTS, VIDEOS } from "@/data";

const FILTERS = ["ALL", "VIDEO", "PHOTO", "DESIGN", "SOCIAL MEDIA", "CAMPAIGN", "PERSONAL"];

const Creative = () => {
  const [filter, setFilter] = useState("ALL");
  const shown = filter === "ALL" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <motion.main
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-ink pt-28 text-paper"
      data-testid="creative-page"
    >
      <header className="px-6 md:px-12">
        <SectionTag no="04" label="THE CREATIVE STUDIO" dark />
        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="creative-title">
          <MaskedLine delay={0.1}><span>I LIKE TO</span></MaskedLine>
          <MaskedLine delay={0.25}><span className="text-ember">MAKE THINGS.</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl font-serif text-xl italic leading-relaxed text-paper/60 md:text-2xl">
            {CREATIVE_INTRO.copy}
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {CREATIVE_INTRO.pillars.map((p, i) => (
            <div key={p.key} className="bg-ink p-6 transition-colors duration-300 hover:bg-coal md:p-8" data-testid={`pillar-${p.key.toLowerCase()}`}>
              <span className="font-display text-xs text-ember">0{i + 1}</span>
              <h3 className="mt-3 font-display text-xl font-black uppercase tracking-tight">{p.key}</h3>
              <p className="mt-2 text-sm text-paper/50">{p.desc}</p>
            </div>
          ))}
        </div>
      </header>

      <section className="px-6 py-24 md:px-12" data-testid="portfolio-section">
        <SectionTag no="01" label="SELECTED WORK — EDITABLE" dark />
        <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="portfolio-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`shrink-0 border px-5 py-2.5 font-display text-[10px] font-bold uppercase tracking-[0.25em] transition-colors duration-300 ${
                filter === f ? "border-ember bg-ember text-ink" : "border-white/20 text-paper/60 hover:border-paper hover:text-paper"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <article className="group" data-testid={`project-${p.id}`}>
                <MediaSlot
                  src={p.src}
                  slot={`${p.id.toUpperCase()}_THUMBNAIL`}
                  ratio={i % 3 === 0 ? "aspect-[4/5]" : "aspect-[4/3]"}
                  dark
                  cursorLabel="EXPLORE"
                />
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <span className="font-display text-[9px] font-bold uppercase tracking-[0.3em] text-ember">{p.category}</span>
                    <h3 className="mt-1 font-display text-lg font-black uppercase tracking-tight">{p.title}</h3>
                    <p className="mt-1 text-xs text-paper/50">{p.desc}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-paper/40">{p.tools} · {p.year || "[ADD YEAR]"}</p>
                  </div>
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noreferrer" aria-label="open project" className="text-paper/50 hover:text-ember">
                      <ArrowUpRight className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 px-6 py-24 md:px-12" data-testid="videos-section">
        <SectionTag no="02" label="MOVING STORIES" dark />
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {VIDEOS.map((v) => (
            <Reveal key={v.id}>
              <article data-testid={`video-${v.id}`}>
                {v.videoSrc ? (
                  <video src={v.videoSrc} poster={v.src || undefined} controls playsInline className="aspect-[9/16] w-full border border-white/15 object-cover" data-cursor="PLAY" />
                ) : (
                  <MediaSlot src={v.src} slot={`${v.id.toUpperCase()}_VIDEO`} video ratio="aspect-[9/16]" dark />
                )}
                <h3 className="mt-4 font-display text-lg font-black uppercase tracking-tight">{v.title}</h3>
                <p className="mt-1 text-xs text-paper/50">{v.desc}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-paper/40">{v.tools} · {v.year || "[ADD YEAR]"}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-xs text-paper/40">Slot video mendukung format vertikal (social media content). Ganti videoSrc di data.js.</p>
      </section>

      <section className="border-t border-white/10 px-6 py-24 md:px-12" data-testid="instagram-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionTag no="04" label="INSTAGRAM" dark />
            <h2 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter md:text-6xl" data-testid="instagram-handle">
              {INSTAGRAM.handle}
            </h2>
            <p className="mt-4 max-w-md font-serif text-lg italic text-paper/50">{INSTAGRAM.sub}</p>
          </div>
          <a
            href={LINKS.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="visit-instagram-button"
            data-cursor="VISIT"
            className="group inline-flex items-center gap-3 bg-paper px-7 py-4 font-display text-xs font-black uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ember"
          >
            <Instagram className="h-4 w-4" /> Visit {INSTAGRAM.handle}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {INSTAGRAM.posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.04} className={i % 2 === 1 ? "md:translate-y-6" : ""}>
              <a href={p.url || LINKS.instagram} target="_blank" rel="noreferrer" data-testid={`instagram-post-${p.id}`} data-cursor="VISIT" className="block">
                <MediaSlot src={p.src} videoSrc={p.videoSrc} slot={`${p.id.toUpperCase()}_CONTENT`} ratio="aspect-square" dark />
                <p className="mt-2 font-display text-[9px] font-bold uppercase tracking-[0.25em] text-ember">{p.kind}</p>
                <p className="text-[11px] text-paper/50">{p.caption}</p>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </motion.main>
  );
};

export default Creative;
